import SEO from "@/components/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Droplet, Shield, Clock, MapPin, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { phlebotomyServices, nearlabPricing } from "@/data/serviceData";
import { useNavigate } from "react-router-dom";
import phlebotomyVials from "@/assets/phlebotomy-vials.jpg";
import phlebotomyDraw from "@/assets/phlebotomy-draw.jpg";
import SetmoreBooking from "@/components/SetmoreBooking";

const Phlebotomy = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO 
        title="Phlebotomy Services in Houston, TX | NearLab Services"
        description="Certified mobile and in-office phlebotomy services in Houston, Richmond, Sugar Land, and nearby areas. Quick booking, transparent pricing, and CLIA/OSHA compliance."
      />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-blue-light/30 to-green-light/30">
        {/* Hero Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-blue-primary/10 via-transparent to-green-primary/10 border-b border-border">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center justify-center md:justify-start gap-3 mb-4 flex-wrap">
                  <Badge variant="outline" className="bg-blue-light/50 text-blue-primary border-blue-primary/20">
                    CLIA-Waived
                  </Badge>
                  <Badge variant="outline" className="bg-green-light/50 text-green-primary border-green-primary/20">
                    OSHA Compliant
                  </Badge>
                  <Badge variant="outline" className="bg-blue-light/50 text-blue-primary border-blue-primary/20">
                    Certified Phlebotomists
                  </Badge>
                </div>
                
                <h1 className="text-4xl md:text-5xl font-bold text-center md:text-left bg-gradient-to-r from-blue-primary to-green-primary bg-clip-text text-transparent mb-4">
                  Phlebotomy Services in Houston, TX
                </h1>
                
                <p className="text-lg text-muted-foreground text-center md:text-left mb-6">
                  Convenient, professional <strong>mobile and in-office blood draws</strong> across Houston, Richmond, Sugar Land, and nearby areas. 
                  We collect your samples and deliver to CLIA-certified partner labs for testing.
                </p>
                
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <Button 
                    size="lg" 
                    onClick={() => navigate('/booking')}
                    className="bg-gradient-blue-green hover:opacity-90"
                  >
                    Book an Appointment
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => window.location.href = 'tel:7135104442'}
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    Call 713-510-4442
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src={phlebotomyVials} 
                  alt="Professional blood sample collection with vials" 
                  className="rounded-lg shadow-lg object-cover h-64 w-full"
                />
                <img 
                  src={phlebotomyDraw} 
                  alt="Certified phlebotomist performing blood draw" 
                  className="rounded-lg shadow-lg object-cover h-64 w-full mt-8"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-2 border-blue-primary/20 hover:border-blue-primary/40 transition-all">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Droplet className="h-5 w-5 text-blue-primary" />
                    <span className="text-xs font-bold text-blue-primary uppercase tracking-wider">What we do</span>
                  </div>
                  <CardTitle className="text-2xl">Professional Specimen Collection</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {phlebotomyServices.map((service, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-green-primary font-bold mt-0.5">✓</span>
                        <div>
                          <span className="font-medium">{service.title}</span>
                          <p className="text-sm text-muted-foreground">{service.description}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-muted-foreground mt-4 pt-4 border-t">
                    All specimens are processed or delivered to <strong>CLIA-certified laboratories</strong> for accurate results.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-primary/20 hover:border-green-primary/40 transition-all">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-5 w-5 text-green-primary" />
                    <span className="text-xs font-bold text-green-primary uppercase tracking-wider">Mobile Advantage</span>
                  </div>
                  <CardTitle className="text-2xl">We Come to You</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold mt-0.5">✓</span>
                      <span>Same-day / next-day appointments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold mt-0.5">✓</span>
                      <span>Home, office, or facility visits</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold mt-0.5">✓</span>
                      <span>Early morning & evening availability</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold mt-0.5">✓</span>
                      <span>Strict temperature control & chain of custody</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold mt-0.5">✓</span>
                      <span>Friendly, professional service every time</span>
                    </li>
                  </ul>
                  <Button 
                    onClick={() => navigate('/booking')}
                    className="w-full bg-gradient-blue-green hover:opacity-90"
                  >
                    Schedule Mobile Draw
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Pricing Table */}
        <section className="py-12 px-4 bg-background/50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-xs font-bold text-blue-primary uppercase tracking-wider">Transparent Pricing</span>
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-primary to-green-primary bg-clip-text text-transparent">
                Phlebotomy Service Pricing
              </h2>
            </div>

            <Card>
              <CardContent className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold">Service</th>
                        <th className="text-left py-3 px-4 font-semibold">Mobile</th>
                        <th className="text-left py-3 px-4 font-semibold">In-Office</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4">Routine Blood Draw (1–3 tubes)</td>
                        <td className="py-3 px-4 font-semibold text-blue-primary">${nearlabPricing.phlebotomy.routineBloodDraw.mobile}</td>
                        <td className="py-3 px-4 font-semibold text-green-primary">${nearlabPricing.phlebotomy.routineBloodDraw.inOffice}</td>
                      </tr>
                      <tr className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4">Specialty / Kit Collection</td>
                        <td className="py-3 px-4 font-semibold text-blue-primary">${nearlabPricing.phlebotomy.specialtyCollection.mobile}</td>
                        <td className="py-3 px-4 font-semibold text-green-primary">${nearlabPricing.phlebotomy.specialtyCollection.inOffice}</td>
                      </tr>
                      <tr className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4">STAT / After Hours</td>
                        <td className="py-3 px-4 font-semibold text-blue-primary">+ ${nearlabPricing.phlebotomy.statAfterHours.mobile}</td>
                        <td className="py-3 px-4 font-semibold text-green-primary">+ ${nearlabPricing.phlebotomy.statAfterHours.inOffice}</td>
                      </tr>
                      <tr className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4">Specimen Drop-Off (Quest/LabCorp)</td>
                        <td className="py-3 px-4 font-semibold text-blue-primary">${nearlabPricing.phlebotomy.specimenDropOff.mobile}</td>
                        <td className="py-3 px-4 font-semibold text-green-primary">${nearlabPricing.phlebotomy.specimenDropOff.inOffice}</td>
                      </tr>
                      <tr className="hover:bg-muted/50">
                        <td className="py-3 px-4">DNA / Drug Test with Blood Draw</td>
                        <td className="py-3 px-4 font-semibold text-blue-primary">${nearlabPricing.phlebotomy.dnaWithBloodDraw.mobile}</td>
                        <td className="py-3 px-4 font-semibold text-green-primary">${nearlabPricing.phlebotomy.dnaWithBloodDraw.inOffice}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-muted-foreground mt-4 pt-4 border-t">
                  Travel fees may vary by location. Group, employer, and recurring patient discounts are available.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Additional Info Cards */}
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <Badge variant="outline" className="w-fit mb-2">For Providers</Badge>
                  <CardTitle>Partner Solutions</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    We support physicians, telemedicine, employers, and research clinics with reliable collections and custom scheduling.
                  </p>
                  <ul className="space-y-2 text-sm mb-4">
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold">✓</span>
                      <span>Standing orders & recurring routes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold">✓</span>
                      <span>On-site events for employers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold">✓</span>
                      <span>Specimen pickup & courier to partner labs</span>
                    </li>
                  </ul>
                  <div className="text-sm space-y-1">
                    <p><strong>Email:</strong> <a href="mailto:info@nearlabservices.com" className="text-blue-primary hover:underline">info@nearlabservices.com</a></p>
                    <p><strong>Phone:</strong> <a href="tel:7135104442" className="text-blue-primary hover:underline">713-510-4442</a></p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <Badge variant="outline" className="w-fit mb-2">Why NearLab</Badge>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-primary" />
                    Trusted & Compliant
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold">✓</span>
                      <span>Certified & insured phlebotomists</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold">✓</span>
                      <span>CLIA-waived & OSHA-compliant</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold">✓</span>
                      <span>Strict HIPAA privacy practices</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <Badge variant="outline" className="w-fit mb-2">Prep</Badge>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-green-primary" />
                    Before Your Appointment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold">✓</span>
                      <span>Bring a photo ID and lab order (if applicable)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold">✓</span>
                      <span>Hydrate well; fasting only if your provider requires it</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-primary font-bold">✓</span>
                      <span>Have your insurance card for lab billing (if applicable)</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Booking Section */}
        <section className="py-12 px-4 bg-background/50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8">
              <span className="text-xs font-bold text-blue-primary uppercase tracking-wider">Book Now</span>
              <h2 className="text-3xl font-bold mt-2 mb-4">Schedule Your Appointment</h2>
              <p className="text-muted-foreground">
                Choose your preferred time for mobile or in-office phlebotomy services
              </p>
            </div>
            <SetmoreBooking />
          </div>
        </section>

        {/* Service Areas & Contact */}
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-8">
              <span className="text-xs font-bold text-blue-primary uppercase tracking-wider">Service Areas</span>
              <h2 className="text-3xl font-bold mt-2 mb-4">Where We Operate</h2>
              <p className="text-muted-foreground">
                Houston • Richmond • Sugar Land • Missouri City • Katy • Rosenberg • Stafford • Pearland • Surrounding areas
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.location.href = 'tel:7135104442'}>
                <CardContent className="p-6 text-center">
                  <h3 className="font-semibold text-lg mb-2">Call / Text: 713-510-4442</h3>
                  <p className="text-sm text-muted-foreground">Speak with a NearLab coordinator.</p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.location.href = 'mailto:info@nearlabservices.com'}>
                <CardContent className="p-6 text-center">
                  <h3 className="font-semibold text-lg mb-2">Email: info@nearlabservices.com</h3>
                  <p className="text-sm text-muted-foreground">Share your order or request a quote.</p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/booking')}>
                <CardContent className="p-6 text-center">
                  <h3 className="font-semibold text-lg mb-2">Alternative Booking</h3>
                  <p className="text-sm text-muted-foreground">Use our full appointment form.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-8">
              <span className="text-xs font-bold text-blue-primary uppercase tracking-wider">FAQ</span>
              <h2 className="text-3xl font-bold mt-2">Common Questions</h2>
            </div>

            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Do you accept insurance?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    We charge a service fee for collection. If your testing lab accepts insurance, they will bill your plan for the laboratory testing itself. 
                    Bring your insurance card to your appointment if applicable.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Can you draw using my kit or my provider's order?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Yes. We routinely collect using provider orders and third-party kits (Quest, LabCorp, Ulta, etc.) and deliver specimens per kit instructions.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">How soon can I schedule?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Same-day and next-day appointments are often available. For early morning fasting draws, please book in advance.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Do you serve facilities and employers?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Yes. We set up on-site events, recurring routes, and custom billing for employers, clinics, and research sites.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Phlebotomy;
